import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import type { components } from '@/api/schema'
import { responseError } from '@/lib/api-error'

// Mirrors the Shopify theme's frontend/lib/storefront-api.ts so this page sends the storefront's
// exact requests. Public endpoints deliberately use plain fetch, not the dashboard auth client.

export type ShopifySearchResponse = components['schemas']['ShopifySearchResponse']
export type ShopifySearchProduct = components['schemas']['ShopifySearchProduct']
/** Catalog Search results (staff smart search) also carry each product's ship time. */
export type CatalogSearchResponse = components['schemas']['CatalogSearchResponse']
export type CatalogSearchProduct = components['schemas']['CatalogSearchProduct']
export type ShopifyFilterOptionsResponse = components['schemas']['ShopifyFilterOptionsResponse']
export type ShopifyFilterGroup = components['schemas']['ShopifyFilterGroup']
export type ShopifyFilterOption = components['schemas']['ShopifyFilterOption']
export type ShopifyQuantityPricing = components['schemas']['ShopifyQuantityPricing']
export type KeywordAutocompleteResponse =
  components['schemas']['ShopifyKeywordAutocompleteResponse']

export const STOREFRONT_SORTS = [
  'featured',
  'price_desc',
  'price_asc',
  'relevance',
  'newest',
] as const
export type StorefrontSort = (typeof STOREFRONT_SORTS)[number]

export type RangeSelection = { min?: string; max?: string }
/** Order by a measurement held in any of these fields (``width`` or ``spec.<slug>``). */
export type SmartRank = { by: string[]; direction: 'asc' | 'desc'; label: string }
/** Product dimensions the smart search filters on; the store filters cannot. */
export const DIMENSION_LABELS: Record<string, string> = {
  width: 'Width',
  depth: 'Depth',
  height: 'Height',
}
export type MultiSelections = Record<string, string[]>
export type RangeSelections = Record<string, RangeSelection>

/** A storefront collection page's fixed context: a manufacturer or category collection GID. */
export type StorefrontScope = {
  key: 'manufacturer' | 'category'
  value: string
  label: string
}

export type DiscoveryRequest = {
  query: string
  page: number
  perPage: number
  sort?: StorefrontSort
  multiSelections: MultiSelections
  rangeSelections: RangeSelections
  scope?: StorefrontScope
  rank?: SmartRank
}

export type AutocompleteLimits = {
  products: number
  vendors: number
  categories: number
  suggestions: number
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

function appendMultiSelections(
  params: URLSearchParams,
  selections: MultiSelections,
  scope?: StorefrontScope,
) {
  Object.entries(selections).forEach(([key, rawValues]) => {
    if (scope?.key === key) return
    const values = [...new Set(rawValues.map((value) => value.trim()).filter(Boolean))]
    if (!values.length) return

    if (key.startsWith('spec.')) {
      const slug = key.slice('spec.'.length)
      if (!slug) return
      const [onlyValue] = values
      if (values.length === 1 && (onlyValue === 'true' || onlyValue === 'false')) {
        params.append(`spec_bool[${slug}]`, onlyValue)
        return
      }
      values.forEach((value) => params.append(`spec[${slug}]`, value))
      return
    }

    values.forEach((value) => params.append(key, value))
  })
}

function appendRangeSelections(
  params: URLSearchParams,
  selections: RangeSelections,
  { dimensions = true }: { dimensions?: boolean } = {},
) {
  Object.entries(selections).forEach(([key, selection]) => {
    if (!dimensions && key in DIMENSION_LABELS) return
    const min = selection.min?.trim()
    const max = selection.max?.trim()
    if (key === 'ship_days') {
      if (max) params.set('ship_days_max', max)
      return
    }
    if (key === 'price' || key in DIMENSION_LABELS) {
      if (min) params.set(`${key}_min`, min)
      if (max) params.set(`${key}_max`, max)
      return
    }
    if (!key.startsWith('spec.')) return
    const slug = key.slice('spec.'.length)
    if (!slug) return
    if (min) params.set(`spec_range[${slug}][min]`, min)
    if (max) params.set(`spec_range[${slug}][max]`, max)
  })
}

export function buildSearchParams(request: DiscoveryRequest) {
  const params = new URLSearchParams()
  params.set('page', String(Math.max(1, Math.floor(request.page))))
  params.set('per_page', String(request.perPage))
  params.set('products_limit', String(request.perPage))
  params.set('vendors_limit', '0')
  params.set('categories_limit', '0')
  if (request.sort && request.sort !== 'featured') params.set('sort', request.sort)
  const query = request.query.trim()
  if (query) params.set('q', query)
  if (request.scope) params.append(request.scope.key, request.scope.value)
  appendMultiSelections(params, request.multiSelections, request.scope)
  appendRangeSelections(params, request.rangeSelections)
  request.rank?.by.forEach((field) => params.append('rank_by', field))
  if (request.rank) params.set('rank_direction', request.rank.direction)
  return params
}

export function buildFilterParams(request: DiscoveryRequest) {
  const params = new URLSearchParams()
  params.set('option_limit', '25')
  params.set('spec_limit', '20')
  params.set('scope_to_selected', 'true')
  params.set('ship_days_facet', 'true')
  const query = request.query.trim()
  if (query) params.set('q', query)
  if (request.scope) params.append(`scope_${request.scope.key}`, request.scope.value)
  appendMultiSelections(params, request.multiSelections, request.scope)
  appendRangeSelections(params, request.rangeSelections, { dimensions: false })
  return params
}

export function buildAutocompleteParams(query: string, limits: AutocompleteLimits) {
  return new URLSearchParams({
    q: query.trim().toLowerCase(),
    products_limit: String(limits.products),
    suggestions_limit: String(limits.suggestions),
    vendors_limit: String(limits.vendors),
    categories_limit: String(limits.categories),
  })
}

async function fetchStorefrontJson<T>(
  path: string,
  params: URLSearchParams,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}?${params.toString()}`, {
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!response.ok) {
    throw await responseError(response, `Storefront request failed with HTTP ${response.status}.`)
  }
  return (await response.json()) as T
}

export function filterOptionsQueryOptions(request: DiscoveryRequest) {
  const params = buildFilterParams(request)
  return queryOptions({
    queryKey: ['storefront', 'filter-options', params.toString()],
    queryFn: ({ signal }) =>
      fetchStorefrontJson<ShopifyFilterOptionsResponse>('/shopify/filter-options', params, signal),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
}

export function autocompleteQueryOptions(query: string, limits: AutocompleteLimits) {
  const params = buildAutocompleteParams(query, limits)
  return queryOptions({
    queryKey: ['storefront', 'keyword-autocomplete', params.toString()],
    queryFn: ({ signal }) =>
      fetchStorefrontJson<KeywordAutocompleteResponse>(
        '/shopify/keyword-autocomplete',
        params,
        signal,
      ),
    staleTime: 60_000,
  })
}
